import { useMemo } from 'react'

import { usePartner } from '@/context/PartnerContext'
import { formatPrice as formatPriceBase, priceFormat as priceFormatBase } from 'BrokerTsLib/src/Utils/formatPrice'

/**
 * Price formatting bound to the current partner's market currency — Kč on CZ, € on SK.
 *
 * Prefer this over importing priceFormat/formatPrice from BrokerTsLib directly: those
 * default to CZK and would render Czech currency to a Slovak broker. The currency
 * argument is dropped from the signatures here, so a call keeps its usual shape:
 *
 *   priceFormat(offer.price)              // amount in the market currency
 *   priceFormat(offer.price, zeroLabel)   // zeroLabel takes the 2nd position
 *
 * For pure helpers that cannot call a hook, take a `currencyCode?: string` parameter
 * and pass `currency?.code` down from the component (see InsuranceCompaniesStats/dataUtils.ts).
 */
export const usePriceFormat = () => {
  const { currency } = usePartner()

  return useMemo(() => ({
    currency,

    /** Market currency symbol — 'Kč' or '€' — for input suffixes and unit labels */
    currencyUnit: currency?.unit ?? 'Kč',

    /** '0 Kč' / '0 €' — zeroLabel for amounts that must not read 'zdarma' */
    zeroPriceLabel: `0 ${currency?.unit ?? 'Kč'}`,

    priceFormat: (
      price?: number | string | null,
      zeroLabel?: string,
      emptyChar?: string,
      notAvailable?: boolean
    ) => priceFormatBase(price, currency?.code, zeroLabel, emptyChar, notAvailable),

    formatPrice: (props: {
      price?: number | string | null
      zeroLabel?: string
      emptyChar?: string
      notAvailable?: boolean
    }) => formatPriceBase({ ...props, currency: currency?.code }),
  }), [currency])
}
